// Face-match evidence widgets, shared by KnownSeenPage and the Ops Console.
//
// These lived as private functions inside KnownSeenPage. The Ops Console
// showed only the label ("Known person: Papa") and buried the raw score in a
// 14-key telemetry dump behind the INFO tab, so an operator triaging an alert
// could not see whether a name was a confident decision or a coin-flip.
//
// NOTE: these render score * 100 and assume a 0..1 cosine. That only became
// true once match_face started normalising its probe -- before that the score
// was ||emb|| * cos and would have rendered as "112%" or "2042%".

export function pct(v) {
  return v != null ? `${Math.round(v * 100)}%` : '—';
}

export function ScoreBar({ score, threshold = 0.45 }) {
  const w   = Math.min(Math.round((score ?? 0) * 100), 100);
  const col = score >= threshold ? '#22c55e' : score >= threshold - 0.10 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${w}%`, background: col, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: col, minWidth: 32, textAlign: 'right' }}>{pct(score)}</span>
    </div>
  );
}

export function CandidateList({ candidates, matchedName }) {
  if (!candidates || !Object.keys(candidates).length) return null;
  const sorted = Object.entries(candidates).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 4 }}>All Candidates</div>
      {sorted.map(([name, score]) => (
        <div key={name} style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontSize: 11, color: name === matchedName ? 'var(--text)' : 'var(--text-muted)',
              fontWeight: name === matchedName ? 700 : 400 }}>
              {name === matchedName ? '✓ ' : ''}{name}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pct(score)}</span>
          </div>
          <ScoreBar score={score} />
        </div>
      ))}
    </div>
  );
}

// Identity evidence for one known/banned alert, read off its telemetry.
// Renders nothing unless the record actually carries a score, so older alerts
// written before the matcher recorded one are left alone rather than shown as
// a misleading 0%.
export function FaceMatchEvidence({ telemetry }) {
  if (!telemetry || typeof telemetry !== 'object') return null;
  const score = typeof telemetry.score === 'number' ? telemetry.score : null;
  if (score == null) return null;

  const threshold = typeof telemetry.match_threshold === 'number' ? telemetry.match_threshold : 0.45;
  const margin    = typeof telemetry.match_margin    === 'number' ? telemetry.match_margin    : 0;
  const runner    = telemetry.runner_up;
  const runnerScore = typeof telemetry.runner_up_score === 'number' ? telemetry.runner_up_score : null;

  // A score above 1.0 cannot be a cosine. It means the box is still running a
  // build whose matcher compared an unnormalised embedding, so the number is
  // ||emb|| * cos and the bar it passed was effectively ~0.015. Say so rather
  // than drawing a 2042% bar.
  const impossible = score > 1.0;

  const ambiguous = !impossible && runner != null && runnerScore != null
                    && margin > 0 && (score - runnerScore) < margin;

  return (
    <div className="ind-card">
      <div className="ind-eye" style={{ marginBottom: 8 }}>Face match</div>

      {impossible ? (
        <div style={{ fontSize: 11, color: '#ef4444', lineHeight: 1.5 }}>
          Score {score.toFixed(3)} — not a valid similarity (must be 0–1).
          This alert came from a build that compared an unnormalised embedding,
          so no identity threshold was actually applied. Treat the name as
          unverified.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 700 }}>{telemetry.name || 'match'}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>bar {pct(threshold)}</span>
          </div>
          <ScoreBar score={score} threshold={threshold} />

          {runner != null && runnerScore != null && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>runner-up · {runner}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pct(runnerScore)}</span>
              </div>
              <ScoreBar score={runnerScore} threshold={threshold} />
            </div>
          )}

          {ambiguous && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#f59e0b' }}>
              ⚠ Within {pct(margin)} of {runner} — the identity is a coin-flip
              between two enrolled people.
            </div>
          )}

          {telemetry.match_refused && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
              refused: {telemetry.match_refused}
            </div>
          )}
        </>
      )}
    </div>
  );
}
